import { lazy, Suspense, Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, useParams } from 'react-router-dom';
import { Layout } from './components/layout';
import { PermissionGate } from './components/common/PermissionGate';
import { AuthGuard } from './components/auth/AuthGuard';
import { FeatureGateProvider } from './hooks/useFeatureGate';
import { FeatureGate } from './components/common/FeatureGate';
import { debugLogger } from './utils/debugLogger';
import { darfAutomatischNeuladen } from './utils/chunkNeuladen';

// Live-Debug-Logs: Sammler moeglichst frueh scharfschalten, damit auch Fehler
// waehrend des ersten Renderns erfasst werden (siehe docs/guides/ERP_DEBUG_LOGS.md).
debugLogger.install();

/**
 * Page Loading Fallback Component
 * Wird während des Ladens von lazy-loaded Komponenten angezeigt
 */
const PageLoader = () => (
  <div className="flex items-center justify-center h-screen bg-white dark:bg-gray-900">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      <span className="text-gray-500 dark:text-gray-400 text-sm">Laden...</span>
    </div>
  </div>
);

/**
 * Weiterleitung alter Pfade mit erhaltenen Routen-Parametern (`:picklistId`, `:id`).
 * Ziel OHNE `/app` — der Router trägt den basename selbst (V-0651).
 */
const LegacyWeiterleitung = ({ ziel }: { ziel: string }) => {
  const params = useParams();
  return <Navigate to={ziel.replace(/:(\w+)/g, (_m, k: string) => params[k] ?? '')} replace />;
};

/**
 * Error Boundary that auto-reloads on chunk load failures (after deployment)
 */
class ChunkErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const isChunkError =
      error.message?.includes('Failed to fetch dynamically imported module') ||
      error.message?.includes('Loading chunk') ||
      error.message?.includes('Loading CSS chunk');

    // 🚨 Ohne diese Meldung verschluckt die Fehlergrenze die eigentliche Ausnahme:
    // der Kunde sieht nur „Die Anwendung wurde aktualisiert", und im Support blieb
    // bisher nur der Browser-Harness zum Nachstellen. Ein echter Chunk-Fehler nach
    // einem Deploy ist dagegen erwartbar und wird nicht als Absturz gemeldet.
    if (!isChunkError) {
      debugLogger.reportRenderError(error, info?.componentStack ?? undefined);
    }

    if (isChunkError && darfAutomatischNeuladen()) {
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-screen bg-white dark:bg-gray-900">
          <div className="text-center p-8">
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Die Anwendung wurde aktualisiert.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Seite neu laden
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ============================================================================
// LAZY IMPORTS - Code-Splitting für bessere Performance
// Komponenten werden erst geladen, wenn sie benötigt werden
// ============================================================================

// Auth Pages
const LoginPage = lazy(() => import('./pages/auth/LoginPage').then(m => ({ default: m.LoginPage })));
const AdminLoginPage = lazy(() => import('./pages/auth/AdminLoginPage').then(m => ({ default: m.AdminLoginPage })));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage').then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage').then(m => ({ default: m.ResetPasswordPage })));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage').then(m => ({ default: m.RegisterPage })));
const OnboardingPage = lazy(() => import('./pages/auth/OnboardingPage').then(m => ({ default: m.OnboardingPage })));
const VerifyEmailPage = lazy(() => import('./pages/auth/VerifyEmailPage').then(m => ({ default: m.VerifyEmailPage })));
const BerichtsversandBestaetigenPage = lazy(() => import('./pages/public/BerichtsversandBestaetigenPage'));
const SupportAccessDecisionPage = lazy(() => import('./pages/auth/SupportAccessDecisionPage').then(m => ({ default: m.SupportAccessDecisionPage })));
const EbayAuthCallback = lazy(() => import('./pages/auth/EbayAuthCallback').then(m => ({ default: m.EbayAuthCallback })));

// Customer Portal (B2B - Order Tracking)
const PortalApp = lazy(() => import('./portal/PortalApp').then(m => ({ default: m.PortalApp })));

// My Portal (Unified Auth - License Management)
const MyPortalApp = lazy(() => import('./myportal/MyPortalApp').then(m => ({ default: m.MyPortalApp })));

// BISTime (Standalone Time Tracking App)
const BISTimeApp = lazy(() => import('./bistime/BISTimeApp').then(m => ({ default: m.BISTimeApp })));

// WebPOS (Browser-based Point of Sale)
const WebPosApp = lazy(() => import('./webpos/WebPosApp'));

// Public Pages
const ReceiptView = lazy(() => import('./pages/receipt/ReceiptView').then(m => ({ default: m.ReceiptView })));
const BookingPage = lazy(() => import('./pages/booking/BookingPage').then(m => ({ default: m.BookingPage })));
const VoucherPurchasePage = lazy(() => import('./pages/voucher/VoucherPurchasePage').then(m => ({ default: m.VoucherPurchasePage })));
const BugTrackerPage = lazy(() => import('./pages/marketing/BugTrackerPage').then(m => ({ default: m.BugTrackerPage })));
const BugTrackerDetailPage = lazy(() => import('./pages/marketing/BugTrackerDetailPage').then(m => ({ default: m.BugTrackerDetailPage })));
const AiBridgePage = lazy(() => import('./pages/marketing/AiBridgePage').then(m => ({ default: m.AiBridgePage })));
const AiBridgeSetupPage = lazy(() => import('./pages/marketing/AiBridgeSetupPage').then(m => ({ default: m.AiBridgeSetupPage })));
// Neues BISpicy-Erscheinungsbild (V-1908): Kopf/Fuß wie bispicy.com, erzeugt von tools/website/kopf_fuss (Ziel erp-app)
const BisCiLayout = lazy(() => import('./pages/marketing/components/BisCiLayout').then(m => ({ default: m.BisCiLayout })));
const DocsAPI = lazy(() => import('./pages/marketing/docs/DocsAPI'));
const PayPerUseUsers = lazy(() => import('./pages/marketing/features/PayPerUseUsers').then(m => ({ default: m.PayPerUseUsers })));

// Profile
const ProfilePage = lazy(() => import('./pages/profile/ProfilePage'));

// Core Pages
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard').then(m => ({ default: m.Dashboard })));
const ArticlesListNew = lazy(() => import('./pages/articles/ArticlesListNew').then(m => ({ default: m.ArticlesListNew })));
const ArticleDetail = lazy(() => import('./pages/articles/ArticleDetail').then(m => ({ default: m.ArticleDetail })));
const OrdersList = lazy(() => import('./pages/orders/OrdersList').then(m => ({ default: m.OrdersList })));
const OrderDetail = lazy(() => import('./pages/orders/OrderDetail').then(m => ({ default: m.OrderDetail })));
// Postfach: default export, deshalb ohne .then(...)-Umweg
const Mailbox = lazy(() => import('./pages/mailbox/Mailbox'));
const OrderImportSources = lazy(() => import('./pages/orders/OrderImportSources').then(m => ({ default: m.OrderImportSources })));
const OrderImportTemplates = lazy(() => import('./pages/orders/OrderImportTemplates').then(m => ({ default: m.OrderImportTemplates })));
const OrderImportHistory = lazy(() => import('./pages/orders/OrderImportHistory').then(m => ({ default: m.OrderImportHistory })));
const OrderSyncReview = lazy(() => import('./pages/orders/OrderSyncReview').then(m => ({ default: m.OrderSyncReview })));
const PriceApprovalList = lazy(() => import('./pages/orders/PriceApprovalList').then(m => ({ default: m.PriceApprovalList })));
const ErstattungenOffen = lazy(() => import('./pages/orders/ErstattungenOffen').then(m => ({ default: m.ErstattungenOffen })));
const RepairOrdersOverview = lazy(() => import('./pages/orders/repairs/RepairOrdersOverview'));
const OffersList = lazy(() => import('./pages/offers/OffersList').then(m => ({ default: m.OffersList })));
const OfferDetail = lazy(() => import('./pages/offers/OfferDetail').then(m => ({ default: m.OfferDetail })));

// Inventory Pages
const InventoryLayout = lazy(() => import('./pages/inventory/InventoryLayout').then(m => ({ default: m.InventoryLayout })));
const InventoryOverview = lazy(() => import('./pages/inventory/InventoryOverview').then(m => ({ default: m.InventoryOverview })));
const BatchesOverview = lazy(() => import('./pages/inventory/BatchesOverview').then(m => ({ default: m.BatchesOverview })));
const ClearanceCandidates = lazy(() => import('./pages/articles/ClearanceCandidates').then(m => ({ default: m.ClearanceCandidates })));
const StorageLocationWizard = lazy(() => import('./pages/inventory/StorageLocationWizard').then(m => ({ default: m.StorageLocationWizard })));
const StorageLocations = lazy(() => import('./pages/inventory/StorageLocations').then(m => ({ default: m.StorageLocations })));
const GoodsReceiptOverview = lazy(() => import('./pages/inventory/GoodsReceiptOverview').then(m => ({ default: m.GoodsReceiptOverview })));
const GoodsReceiptPC = lazy(() => import('./pages/inventory/GoodsReceiptPC').then(m => ({ default: m.GoodsReceiptPC })));
const GoodsReceiptMDE = lazy(() => import('./pages/inventory/GoodsReceiptMDE').then(m => ({ default: m.GoodsReceiptMDE })));
const StocktakeOverview = lazy(() => import('./pages/inventory/StocktakeOverview').then(m => ({ default: m.StocktakeOverview })));
const StocktakeForm = lazy(() => import('./pages/inventory/StocktakeForm').then(m => ({ default: m.StocktakeForm })));
const StocktakeMDE = lazy(() => import('./pages/inventory/StocktakeMDE').then(m => ({ default: m.StocktakeMDE })));
const StocktakePrint = lazy(() => import('./pages/inventory/StocktakePrint').then(m => ({ default: m.StocktakePrint })));
const StocktakeReview = lazy(() => import('./pages/inventory/StocktakeReview').then(m => ({ default: m.StocktakeReview })));
const CycleCountSchedule = lazy(() => import('./pages/inventory/CycleCountSchedule').then(m => ({ default: m.CycleCountSchedule })));
const StockAdjustment = lazy(() => import('./pages/inventory/StockAdjustment').then(m => ({ default: m.StockAdjustment })));
const StockAdjustmentMDE = lazy(() => import('./pages/inventory/StockAdjustmentMDE').then(m => ({ default: m.StockAdjustmentMDE })));
const InventorySnapshot = lazy(() => import('./pages/inventory/InventorySnapshot').then(m => ({ default: m.InventorySnapshot })));
const StorageLocationLabelPrint = lazy(() => import('./pages/inventory/StorageLocationLabelPrint').then(m => ({ default: m.StorageLocationLabelPrint })));
// Der frühere Lagerplatz-Etikettendesigner ist entfallen — Etiketten werden im
// Design Studio (/settings/design-studio?category=label) gestaltet.

// Hazmat Pages
const HazmatOverview = lazy(() => import('./pages/articles/HazmatOverview').then(m => ({ default: m.HazmatOverview })));
const LmivReviewList = lazy(() => import('./pages/articles/LmivReviewList').then(m => ({ default: m.LmivReviewList })));
const Fotoauftraege = lazy(() => import('./pages/articles/Fotoauftraege').then(m => ({ default: m.Fotoauftraege })));

// Fulfillment Pages
const PackingStation = lazy(() => import('./pages/packing/PackingStation').then(m => ({ default: m.PackingStation })));
const OpenPackDrafts = lazy(() => import('./pages/shipping/OpenPackDrafts').then(m => ({ default: m.OpenPackDrafts })));
const PickingOverview = lazy(() => import('./pages/picking/PickingOverview').then(m => ({ default: m.PickingOverview })));
const PickingMDE = lazy(() => import('./pages/picking/PickingMDE').then(m => ({ default: m.PickingMDE })));
const PicklistPrint = lazy(() => import('./pages/picking/PicklistPrint').then(m => ({ default: m.PicklistPrint })));
const WavePickingMDE = lazy(() => import('./pages/picking/WavePickingMDE').then(m => ({ default: m.WavePickingMDE })));
const WavePackingStation = lazy(() => import('./pages/picking/WavePackingStation').then(m => ({ default: m.WavePackingStation })));
const PackStationDashboard = lazy(() => import('./pages/picking/PackStationDashboard').then(m => ({ default: m.PackStationDashboard })));
const CartOverview = lazy(() => import('./pages/picking/CartOverview').then(m => ({ default: m.CartOverview })));
const ShippingLayout = lazy(() => import('./pages/shipping/ShippingLayout').then(m => ({ default: m.ShippingLayout })));
const ShippingOverview = lazy(() => import('./pages/shipping/ShippingOverview').then(m => ({ default: m.ShippingOverview })));
const DpdSettings = lazy(() => import('./pages/shipping/DpdSettings').then(m => ({ default: m.DpdSettings })));
const FlashExpressSettings = lazy(() => import('./pages/shipping/FlashExpressSettings').then(m => ({ default: m.FlashExpressSettings })));
const KerryExpressSettings = lazy(() => import('./pages/shipping/KerryExpressSettings').then(m => ({ default: m.KerryExpressSettings })));
const DeliveryNotes = lazy(() => import('./pages/shipping/DeliveryNotes').then(m => ({ default: m.DeliveryNotes })));
const DeliveryNoteDetail = lazy(() => import('./pages/shipping/DeliveryNoteDetail').then(m => ({ default: m.DeliveryNoteDetail })));
const DeliveryNoteSettings = lazy(() => import('./pages/settings/DeliveryNoteSettings').then(m => ({ default: m.DeliveryNoteSettings })));
const RateComparison = lazy(() => import('./pages/shipping/RateComparison').then(m => ({ default: m.RateComparison })));
const ShippingRules = lazy(() => import('./pages/shipping/ShippingRules').then(m => ({ default: m.ShippingRules })));
const ShippingCostReconciliation = lazy(() => import('./pages/shipping/ShippingCostReconciliation').then(m => ({ default: m.ShippingCostReconciliation })));

// Replenishment Pages
const ReplenishmentOverview = lazy(() => import('./pages/replenishment/ReplenishmentOverview').then(m => ({ default: m.ReplenishmentOverview })));
const LocationsManagement = lazy(() => import('./pages/replenishment/LocationsManagement').then(m => ({ default: m.LocationsManagement })));
const StockLevelsEditor = lazy(() => import('./pages/replenishment/StockLevelsEditor').then(m => ({ default: m.StockLevelsEditor })));
const ReplenishmentMDE = lazy(() => import('./pages/replenishment/ReplenishmentMDE').then(m => ({ default: m.ReplenishmentMDE })));
const ReceivingMDE = lazy(() => import('./pages/replenishment/ReceivingMDE').then(m => ({ default: m.ReceivingMDE })));

// Cartons (BISCarton) Pages
const CartonStatistics = lazy(() => import('./pages/cartons/CartonStatistics').then(m => ({ default: m.CartonStatistics })));
const PPWRCompliance = lazy(() => import('./pages/cartons/PPWRCompliance').then(m => ({ default: m.PPWRCompliance })));

// Purchasing Pages
const SupplierManagement = lazy(() => import('./pages/purchasing/SupplierManagement').then(m => ({ default: m.SupplierManagement })));
const PurchaseOrderManagement = lazy(() => import('./pages/purchasing/PurchaseOrderManagement').then(m => ({ default: m.PurchaseOrderManagement })));
const PurchasingDashboard = lazy(() => import('./pages/purchasing/PurchasingDashboard').then(m => ({ default: m.PurchasingDashboard })));
const ProcurementNeeds = lazy(() => import('./pages/purchasing/ProcurementNeeds').then(m => ({ default: m.ProcurementNeeds })));
const ReceiptCosts = lazy(() => import('./pages/purchasing/ReceiptCosts').then(m => ({ default: m.ReceiptCosts })));
const OrderSuggestionsOverview = lazy(() => import('./pages/purchasing/OrderSuggestionsOverview').then(m => ({ default: m.OrderSuggestionsOverview })));
const SupplierPricesManagement = lazy(() => import('./pages/purchasing/SupplierPricesManagement').then(m => ({ default: m.SupplierPricesManagement })));
const PurchasingAnalytics = lazy(() => import('./pages/purchasing/PurchasingAnalytics').then(m => ({ default: m.PurchasingAnalytics })));
const AbcAnalysis = lazy(() => import('./pages/purchasing/AbcAnalysis').then(m => ({ default: m.AbcAnalysis })));
const QuickPriceEntry = lazy(() => import('./pages/purchasing/QuickPriceEntry').then(m => ({ default: m.QuickPriceEntry })));

// Returns Pages
const ReturnsOverview = lazy(() => import('./pages/returns/ReturnsOverview'));
const WorkshopOrdersOverview = lazy(() => import('./pages/returns/WorkshopOrdersOverview'));

// Tasks Pages
const TasksList = lazy(() => import('./pages/tasks/TasksList'));
const TasksBoard = lazy(() => import('./pages/tasks/TasksBoard'));
const TasksCalendar = lazy(() => import('./pages/tasks/TasksCalendar'));
const MyTasks = lazy(() => import('./pages/tasks/MyTasks'));

// Workflows Pages
const WorkflowsList = lazy(() => import('./pages/workflows').then(m => ({ default: m.WorkflowsList })));
const WorkflowDetail = lazy(() => import('./pages/workflows').then(m => ({ default: m.WorkflowDetail })));

// Invoice/Accounting Pages
const InvoicesList = lazy(() => import('./pages/invoices/InvoicesList').then(m => ({ default: m.InvoicesList })));
const InvoiceDetail = lazy(() => import('./pages/invoices/InvoiceDetail').then(m => ({ default: m.InvoiceDetail })));
const InvoiceForm = lazy(() => import('./pages/invoices/InvoiceForm').then(m => ({ default: m.InvoiceForm })));
const CollectiveInvoiceForm = lazy(() => import('./pages/invoices/CollectiveInvoiceForm').then(m => ({ default: m.CollectiveInvoiceForm })));
const IncomingInvoicesList = lazy(() => import('./pages/incoming-invoices/IncomingInvoicesList').then(m => ({ default: m.IncomingInvoicesList })));
const IncomingInvoiceDetail = lazy(() => import('./pages/incoming-invoices/IncomingInvoiceDetail').then(m => ({ default: m.IncomingInvoiceDetail })));
const PaymentsOverview = lazy(() => import('./pages/payments/PaymentsOverview').then(m => ({ default: m.PaymentsOverview })));
const AccountingOverview = lazy(() => import('./pages/accounting/AccountingOverview').then(m => ({ default: m.AccountingOverview })));
const DatevExport = lazy(() => import('./pages/accounting/DatevExport').then(m => ({ default: m.DatevExport })));
const DatevOnline = lazy(() => import('./pages/accounting/DatevOnline').then(m => ({ default: m.DatevOnline })));
const LexwareOffice = lazy(() => import('./pages/accounting/LexwareOffice').then(m => ({ default: m.LexwareOffice })));
const Verfahrensdokumentation = lazy(() => import('./pages/accounting/Verfahrensdokumentation').then(m => ({ default: m.Verfahrensdokumentation })));
const Bookkeeping = lazy(() => import('./pages/accounting/Bookkeeping').then(m => ({ default: m.Bookkeeping })));
const Differenzbesteuerung = lazy(() => import('./pages/accounting/Differenzbesteuerung').then(m => ({ default: m.Differenzbesteuerung })));
const FinancialReports = lazy(() => import('./pages/reports/FinancialReports').then(m => ({ default: m.FinancialReports })));

// CRM Layout
const CrmLayout = lazy(() => import('./pages/crm/CrmLayout').then(m => ({ default: m.CrmLayout })));
const CrmOverview = lazy(() => import('./pages/crm/CrmOverview').then(m => ({ default: m.CrmOverview })));
const TerritoriesPage = lazy(() => import('./pages/crm/TerritoriesPage').then(m => ({ default: m.TerritoriesPage })));
const DuplicatesPage = lazy(() => import('./pages/crm/DuplicatesPage').then(m => ({ default: m.DuplicatesPage })));
const TodayList = lazy(() => import('./pages/crm/TodayList').then(m => ({ default: m.TodayList })));
const CallModus = lazy(() => import('./pages/crm/CallModus').then(m => ({ default: m.CallModus })));
const TourPreviewList = lazy(() => import('./pages/crm/TourPreviewList').then(m => ({ default: m.TourPreviewList })));
const AbcDistribution = lazy(() => import('./pages/crm/AbcDistribution').then(m => ({ default: m.AbcDistribution })));
const PipelinePage = lazy(() => import('./pages/crm/PipelinePage').then(m => ({ default: m.PipelinePage })));

// Customer Pages
const CustomersList = lazy(() => import('./pages/customers').then(m => ({ default: m.CustomersList })));
const CustomerDetail = lazy(() => import('./pages/customers').then(m => ({ default: m.CustomerDetail })));
const ShelfFeedback = lazy(() => import('./pages/customers').then(m => ({ default: m.ShelfFeedback })));

// Tax Pages
const TaxOverview = lazy(() => import('./pages/taxes/TaxOverview').then(m => ({ default: m.TaxOverview })));
const TaxClassesManagement = lazy(() => import('./pages/taxes/TaxClassesManagement').then(m => ({ default: m.TaxClassesManagement })));
const ZMReports = lazy(() => import('./pages/taxes/ZMReports').then(m => ({ default: m.ZMReports })));
const WarehouseVatCompliance = lazy(() => import('./pages/taxes/WarehouseVatCompliance').then(m => ({ default: m.WarehouseVatCompliance })));
const ProofOfArrival = lazy(() => import('./pages/taxes/ProofOfArrival').then(m => ({ default: m.ProofOfArrival })));

// WooCommerce Integration Pages
const WooCommerceDashboard = lazy(() => import('./pages/integrations/woocommerce').then(m => ({ default: m.WooCommerceDashboard })));
const WooCommerceConnections = lazy(() => import('./pages/integrations/woocommerce').then(m => ({ default: m.WooCommerceConnections })));
const WooCommerceProducts = lazy(() => import('./pages/integrations/woocommerce').then(m => ({ default: m.WooCommerceProducts })));
const WooCommerceOrders = lazy(() => import('./pages/integrations/woocommerce').then(m => ({ default: m.WooCommerceOrders })));
const WooCommerceBom = lazy(() => import('./pages/integrations/woocommerce').then(m => ({ default: m.WooCommerceBom })));

// Shopware 6 Integration Pages
const ShopwareDashboard = lazy(() => import('./pages/integrations/shopware').then(m => ({ default: m.ShopwareDashboard })));
const ShopwareConnections = lazy(() => import('./pages/integrations/shopware').then(m => ({ default: m.ShopwareConnections })));
const ShopwareProducts = lazy(() => import('./pages/integrations/shopware').then(m => ({ default: m.ShopwareProducts })));
const ShopwareOrders = lazy(() => import('./pages/integrations/shopware').then(m => ({ default: m.ShopwareOrders })));
const ShopwareBom = lazy(() => import('./pages/integrations/shopware').then(m => ({ default: m.ShopwareBom })));

// Shopify Integration
const ShopifyIntegration = lazy(() => import('./pages/settings/ShopifyIntegration'));
const WiderrufBridgeSettings = lazy(() => import('./pages/settings/WiderrufBridgeSettings'));

// Magento 2 Integration Pages
const MagentoDashboard = lazy(() => import('./pages/integrations/magento').then(m => ({ default: m.MagentoDashboard })));
const MagentoConnections = lazy(() => import('./pages/integrations/magento').then(m => ({ default: m.MagentoConnections })));
const MagentoProducts = lazy(() => import('./pages/integrations/magento').then(m => ({ default: m.MagentoProducts })));
const MagentoOrders = lazy(() => import('./pages/integrations/magento').then(m => ({ default: m.MagentoOrders })));
const OdooDashboard = lazy(() => import('./pages/integrations/odoo').then(m => ({ default: m.OdooDashboard })));
const OdooConnections = lazy(() => import('./pages/integrations/odoo').then(m => ({ default: m.OdooConnections })));
const OdooMappings = lazy(() => import('./pages/integrations/odoo').then(m => ({ default: m.OdooMappings })));
const OdooErrors = lazy(() => import('./pages/integrations/odoo').then(m => ({ default: m.OdooErrors })));
const OdooSyncLog = lazy(() => import('./pages/integrations/odoo').then(m => ({ default: m.OdooSyncLog })));
const OdooMigration = lazy(() => import('./pages/integrations/odoo').then(m => ({ default: m.OdooMigration })));

// JTL-Fulfillment-Network (FFN)
const FfnIntegration = lazy(() => import('./pages/integrations/ffn/FfnIntegration').then(m => ({ default: m.FfnIntegration })));

// Etsy Integration Pages
const EtsyDashboard = lazy(() => import('./pages/integrations/etsy').then(m => ({ default: m.EtsyDashboard })));
const EtsyConnections = lazy(() => import('./pages/integrations/etsy').then(m => ({ default: m.EtsyConnections })));
const EtsyListings = lazy(() => import('./pages/integrations/etsy').then(m => ({ default: m.EtsyListings })));
const EtsyListingDetail = lazy(() => import('./pages/integrations/etsy').then(m => ({ default: m.EtsyListingDetail })));
const EtsyOrders = lazy(() => import('./pages/integrations/etsy').then(m => ({ default: m.EtsyOrders })));
const EtsySettings = lazy(() => import('./pages/integrations/etsy').then(m => ({ default: m.EtsySettings })));

// Shopee Integration Pages
const ShopeeDashboard = lazy(() => import('./pages/integrations/shopee').then(m => ({ default: m.ShopeeDashboard })));
const ShopeeConnections = lazy(() => import('./pages/integrations/shopee').then(m => ({ default: m.ShopeeConnections })));
const ShopeeProducts = lazy(() => import('./pages/integrations/shopee').then(m => ({ default: m.ShopeeProducts })));
const ShopeeOrders = lazy(() => import('./pages/integrations/shopee').then(m => ({ default: m.ShopeeOrders })));
const ShopeeSettings = lazy(() => import('./pages/integrations/shopee').then(m => ({ default: m.ShopeeSettings })));

// Whatnot Integration (Beta, V-1727)
const WhatnotOverview = lazy(() => import('./pages/integrations/whatnot').then(m => ({ default: m.WhatnotOverview })));

// Google Local Inventory (BISLocal) Pages
const GoogleLocalDashboard = lazy(() => import('./pages/integrations/google-local').then(m => ({ default: m.GoogleLocalDashboard })));
const GoogleLocalSettings = lazy(() => import('./pages/integrations/google-local').then(m => ({ default: m.GoogleLocalSettings })));
const GoogleLocalArticles = lazy(() => import('./pages/integrations/google-local').then(m => ({ default: m.GoogleLocalArticles })));

// Dropshipping Pages
const DropshipDashboard = lazy(() => import('./pages/dropshipping').then(m => ({ default: m.DropshipDashboard })));
const DropshipPrincipals = lazy(() => import('./pages/dropshipping').then(m => ({ default: m.DropshipPrincipals })));
const DropshipPrincipalDetail = lazy(() => import('./pages/dropshipping').then(m => ({ default: m.DropshipPrincipalDetail })));
const DropshipOrders = lazy(() => import('./pages/dropshipping').then(m => ({ default: m.DropshipOrders })));
const DropshipImport = lazy(() => import('./pages/dropshipping').then(m => ({ default: m.DropshipImport })));
const DropshipMappings = lazy(() => import('./pages/dropshipping').then(m => ({ default: m.DropshipMappings })));
const DropshipBilling = lazy(() => import('./pages/dropshipping').then(m => ({ default: m.DropshipBilling })));

// Banking Pages
const BankingOverview = lazy(() => import('./pages/banking').then(m => ({ default: m.BankingOverview })));

// Repricing (BISRepricer) Pages
const RepricingDashboard = lazy(() => import('./pages/repricing').then(m => ({ default: m.RepricingDashboard })));
const RepricingRules = lazy(() => import('./pages/repricing').then(m => ({ default: m.RepricingRules })));
const RepricingHistory = lazy(() => import('./pages/repricing').then(m => ({ default: m.RepricingHistory })));
const RepricingArticles = lazy(() => import('./pages/repricing').then(m => ({ default: m.RepricingArticles })));
const CompetitorMonitoring = lazy(() => import('./pages/repricing').then(m => ({ default: m.CompetitorMonitoring })));
const PriceCrawler = lazy(() => import('./pages/repricing').then(m => ({ default: m.PriceCrawler })));
const MarketAnalysis = lazy(() => import('./pages/repricing').then(m => ({ default: m.MarketAnalysis })));

// BISCalc (Preiskalkulation & Preisschilder)
const BISCalcDashboard = lazy(() => import('./pages/biscalc').then(m => ({ default: m.BISCalcDashboard })));
const BISCalcRules = lazy(() => import('./pages/biscalc').then(m => ({ default: m.BISCalcRules })));
const BISCalcArticles = lazy(() => import('./pages/biscalc').then(m => ({ default: m.BISCalcArticles })));
const BISCalcHistory = lazy(() => import('./pages/biscalc').then(m => ({ default: m.BISCalcHistory })));
const BISCalcLabelPrint = lazy(() => import('./pages/biscalc').then(m => ({ default: m.BISCalcLabelPrint })));
const BISCalcReports = lazy(() => import('./pages/biscalc').then(m => ({ default: m.BISCalcReports })));
const BISCalcSettings = lazy(() => import('./pages/biscalc').then(m => ({ default: m.BISCalcSettings })));

// BISPerformance (Mitarbeiter-Produktivitätsanalyse)
const PerformanceDashboard = lazy(() => import('./pages/performance').then(m => ({ default: m.PerformanceDashboard })));
const EmployeeDetail = lazy(() => import('./pages/performance').then(m => ({ default: m.EmployeeDetail })));
const PerformanceReports = lazy(() => import('./pages/performance').then(m => ({ default: m.PerformanceReports })));
const PerformanceSettings = lazy(() => import('./pages/performance').then(m => ({ default: m.PerformanceSettings })));
const VertriebsScorecard = lazy(() => import('./pages/performance').then(m => ({ default: m.VertriebsScorecard })));

// BISJustShip (Standalone Shipping Module)
const BISJustShipOverview = lazy(() => import('./pages/bisship/BISJustShipOverview'));

// Newsletter Pages
const NewsletterOverview = lazy(() => import('./pages/newsletter/NewsletterOverview').then(m => ({ default: m.NewsletterOverview })));

// System Campaigns
const SystemCampaignsOverview = lazy(() => import('./pages/system-campaigns/SystemCampaignsOverview').then(m => ({ default: m.SystemCampaignsOverview })));

// POS Reports (Tax Reports for POS Kasse)
const PosReports = lazy(() => import('./pages/reports/PosReports').then(m => ({ default: m.PosReports })));

// POS Loyalty (Loyalty Program Management)
const PosLoyalty = lazy(() => import('./pages/pos/PosLoyalty').then(m => ({ default: m.PosLoyalty })));

// POS Integrations — moved to Settings > Schnittstellen (redirects below)

// Reports Pages
const ReportsLayout = lazy(() => import('./pages/reports').then(m => ({ default: m.ReportsLayout })));
const ReportsDashboard = lazy(() => import('./pages/reports').then(m => ({ default: m.ReportsDashboard })));
const SalesReports = lazy(() => import('./pages/reports').then(m => ({ default: m.SalesReports })));
const CustomerReports = lazy(() => import('./pages/reports').then(m => ({ default: m.CustomerReports })));
const ArticleReports = lazy(() => import('./pages/reports').then(m => ({ default: m.ArticleReports })));
const InventoryReports = lazy(() => import('./pages/reports').then(m => ({ default: m.InventoryReports })));
const InventoryValuation = lazy(() => import('./pages/reports').then(m => ({ default: m.InventoryValuation })));
const FulfillmentReports = lazy(() => import('./pages/reports').then(m => ({ default: m.FulfillmentReports })));
const MarketplaceReports = lazy(() => import('./pages/reports').then(m => ({ default: m.MarketplaceReports })));
const TaxReports = lazy(() => import('./pages/reports').then(m => ({ default: m.TaxReports })));
const SupplierReports = lazy(() => import('./pages/reports').then(m => ({ default: m.SupplierReports })));
const FeesReport = lazy(() => import('./pages/reports').then(m => ({ default: m.FeesReport })));

// Listings Pages
const ListingsOverview = lazy(() => import('./pages/listings/ListingsOverview').then(m => ({ default: m.ListingsOverview })));

// Settings Pages
const SettingsOverview = lazy(() => import('./pages/settings/SettingsOverview').then(m => ({ default: m.SettingsOverview })));
// BIS Archiv (revisionssicheres DMS) — geteilte Seite mit dem Kundencenter; in der ERP-App
// lizenz-gegated über FeatureGate feature="dms_revisionssicher".
const BisArchiv = lazy(() => import('./myportal/pages/MyPortalDocumentArchive'));
// Anbindungen — Status aller Schnittstellen (geteilte Seite, kein Lizenz-Gate: Basis-Funktion).
const IntegrationStatusPage = lazy(() => import('./myportal/pages/IntegrationStatusDashboard'));
const EanPoolSettings = lazy(() => import('./pages/settings/EanPoolSettings').then(m => ({ default: m.EanPoolSettings })));
const ManufacturerSettings = lazy(() => import('./pages/settings/ManufacturerSettings').then(m => ({ default: m.ManufacturerSettings })));
const CountrySettings = lazy(() => import('./pages/settings/CountrySettings').then(m => ({ default: m.CountrySettings })));
const JtlMigrationWizard = lazy(() => import('./pages/migration/JtlMigrationWizard').then(m => ({ default: m.JtlMigrationWizard })));
// Datenübernahme-Assistent (Vorsystem-Import, z. B. GastroSoft Pro) — eigener Bereich, NICHT im
// normalen Artikel-Import-Dialog (Ben-Festlegung).
const DatenuebernahmeWizard = lazy(() => import('./pages/migration/DatenuebernahmeWizard').then(m => ({ default: m.DatenuebernahmeWizard })));
const StripeIntegration = lazy(() => import('./pages/settings/StripeIntegration'));
const StripeSubscriptions = lazy(() => import('./pages/stripe/StripeSubscriptions'));
const EanListSettings = lazy(() => import('./pages/settings/EanListSettings').then(m => ({ default: m.EanListSettings })));
const Gs1PrefixSettings = lazy(() => import('./pages/settings/Gs1PrefixSettings').then(m => ({ default: m.Gs1PrefixSettings })));
const Gs1DigitalLink = lazy(() => import('./pages/settings/Gs1Settings').then(m => ({ default: m.Gs1Settings })));
const AiArticleSettings = lazy(() => import('./pages/settings/AiArticleSettings'));

// Tenant Files
const TenantFiles = lazy(() => import('./pages/settings/TenantFiles'));

// Document Templates Pages (BISDoc)
const DocumentTemplates = lazy(() => import('./pages/settings/DocumentTemplates'));
const DocumentTemplateEditor = lazy(() => import('./components/documents/DocumentTemplateEditor').then(m => ({ default: m.default })));

// Invoice Designer (Visual Drag & Drop)
const InvoiceDesigner = lazy(() => import('./pages/settings/InvoiceDesigner'));

// POS Receipt Template Editor
const PosReceiptTemplateEditor = lazy(() => import('./pages/settings/PosReceiptTemplateEditor'));

// Admin Pages
const AdminLayout = lazy(() => import('./components/admin/AdminLayout').then(m => ({ default: m.AdminLayout })));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })));
const CustomersManagement = lazy(() => import('./pages/admin/CustomersManagement'));
const CarrierAPIManagement = lazy(() => import('./pages/admin/CarrierAPIManagement').then(m => ({ default: m.CarrierAPIManagement })));
const MarketplaceAPIManagement = lazy(() => import('./pages/admin/MarketplaceAPIManagement').then(m => ({ default: m.MarketplaceAPIManagement })));
const ServiceAPIManagement = lazy(() => import('./pages/admin/ServiceAPIManagement').then(m => ({ default: m.ServiceAPIManagement })));
const AiAPIManagement = lazy(() => import('./pages/admin/AiAPIManagement').then(m => ({ default: m.AiAPIManagement })));
const SystemLogs = lazy(() => import('./pages/admin/SystemLogs').then(m => ({ default: m.SystemLogs })));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings').then(m => ({ default: m.AdminSettings })));
const SmtpSettings = lazy(() => import('./pages/admin/SmtpSettings').then(m => ({ default: m.SmtpSettings })));
const SupportAccessRequests = lazy(() => import('./pages/admin/SupportAccessRequests').then(m => ({ default: m.SupportAccessRequests })));
const AdminAktionHandyshopSumup = lazy(() => import('./pages/admin/AdminAktionHandyshopSumup'));
const QaDashboard = lazy(() => import('./pages/admin/QaDashboard').then(m => ({ default: m.QaDashboard })));
const DhlAdminSettings = lazy(() => import('./pages/admin/DhlAdminSettings').then(m => ({ default: m.DhlAdminSettings })));
const DpdAdminSettings = lazy(() => import('./pages/admin/DpdAdminSettings').then(m => ({ default: m.DpdAdminSettings })));
const VatRatesManagement = lazy(() => import('./pages/admin/VatRatesManagement').then(m => ({ default: m.VatRatesManagement })));

// Unified Auth & License System Admin Pages
const UsersManagement = lazy(() => import('./pages/admin/UsersManagement'));
const AdminUserDetail = lazy(() => import('./pages/admin/AdminUserDetail'));
const UnifiedLicensesManagement = lazy(() => import('./pages/admin/UnifiedLicensesManagement'));
const SupportTicketsManagement = lazy(() => import('./pages/admin/SupportTicketsManagement'));
const AppNotificationsManagement = lazy(() => import('./pages/admin/AppNotificationsManagement'));
const NewsletterManagement = lazy(() => import('./pages/admin/NewsletterManagement'));
const DevicesManagement = lazy(() => import('./pages/admin/DevicesManagement'));
const AdminTranslatePricing = lazy(() => import('./pages/admin/AdminTranslatePricing'));
const PricingManagement = lazy(() => import('./pages/admin/PricingManagement').then(m => ({ default: m.PricingManagement })));
const ENumberManagement = lazy(() => import('./pages/admin/ENumberManagement').then(m => ({ default: m.ENumberManagement })));
const EmailAccountsManagement = lazy(() => import('./pages/admin/EmailAccountsManagement'));
const AdminCampaigns = lazy(() => import('./pages/admin/AdminCampaigns'));
const AdminMailbox = lazy(() => import('./pages/admin/AdminMailbox'));
const SocialMediaManagement = lazy(() => import('./pages/admin/SocialMediaManagement'));
const SuperAdminUsers = lazy(() => import('./pages/admin/SuperAdminUsers'));
const AdminProfile = lazy(() => import('./pages/admin/AdminProfile'));
const SyncDebug = lazy(() => import('./pages/admin/SyncDebug'));
const SeoAnalytics = lazy(() => import('./pages/admin/SeoAnalytics').then(m => ({ default: m.SeoAnalytics })));
const RatgeberRedaktion = lazy(() => import('./pages/admin/RatgeberRedaktion').then(m => ({ default: m.RatgeberRedaktion })));
const VorhabenBoard = lazy(() => import('./pages/admin/VorhabenBoard'));

// ============================================================================
// APP COMPONENT
// ============================================================================

function App() {
  return (
    <ChunkErrorBoundary>
    {/* basename folgt dem Vite-base (import.meta.env.BASE_URL): '/app/' im Status quo,
        '/' beim Subdomain-Cutover auf erp.bispicy.com. React Router normalisiert den
        abschließenden Slash. */}
    <BrowserRouter basename={import.meta.env.BASE_URL}>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/onboarding" element={<OnboardingPage />} />
          <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
          {/* Berichtsversand: Einladung/Abmeldung aus der Mail — Token ist die Berechtigung, kein Login */}
          <Route path="/berichtsversand/bestaetigen/:token" element={<BerichtsversandBestaetigenPage />} />
          <Route path="/ebay/auth/callback" element={<EbayAuthCallback />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/forgot-password" element={<ForgotPasswordPage isAdmin />} />
          <Route path="/admin/reset-password/:token" element={<ResetPasswordPage isAdmin />} />
          {/* Fernwartungs-Entscheidung aus der Kunden-Mail — Token ist die Berechtigung,
              daher kein Login noetig. Muss unter /app liegen, sonst faengt die
              Marketing-Website den Pfad ab (siehe EmailService::sendSupportAccessRequest). */}
          <Route path="/support-access/approve/:token" element={<SupportAccessDecisionPage mode="approve" />} />
          <Route path="/support-access/reject/:token" element={<SupportAccessDecisionPage mode="reject" />} />

          {/* Public Pages (no auth required) */}
          <Route path="/r/:referralCode/:saleId" element={<ReceiptView />} />
          <Route path="/booking/:businessSlug" element={<BookingPage />} />
          <Route path="/voucher/:slug" element={<VoucherPurchasePage />} />
          <Route element={<BisCiLayout />}>
            <Route path="/ai-bridge" element={<AiBridgePage />} />
            <Route path="/ai-bridge/einrichtung" element={<AiBridgeSetupPage />} />
            <Route path="/bugtracker" element={<BugTrackerPage />} />
            <Route path="/bugtracker/:slug" element={<BugTrackerDetailPage />} />
            <Route path="/docs/api" element={<DocsAPI />} />
            <Route path="/features/pay-per-use" element={<PayPerUseUsers />} />
          </Route>

          {/* Customer Portal - B2B Order Tracking (separate authentication) */}
          <Route path="/portal/*" element={<PortalApp />} />

          {/* My Portal - Unified Auth License Management (my.bispicy.com) */}
          <Route path="/my/*" element={<MyPortalApp />} />
          <Route path="/bistime/*" element={<BISTimeApp />} />
          <Route path="/pos/*" element={<WebPosApp />} />

          {/* Admin Routes (Protected) */}
          <Route path="/admin" element={<AuthGuard><AdminLayout /></AuthGuard>}>
            <Route index element={<AdminDashboard />} />
            <Route path="customers" element={<CustomersManagement />} />
            <Route path="carriers" element={<CarrierAPIManagement />} />
            <Route path="marketplaces" element={<MarketplaceAPIManagement />} />
            <Route path="services" element={<ServiceAPIManagement />} />
            <Route path="ai-apis" element={<AiAPIManagement />} />
            <Route path="logs" element={<SystemLogs />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="smtp-settings" element={<SmtpSettings />} />
            <Route path="support-requests" element={<SupportAccessRequests />} />
            <Route path="aktionen/handyshop-sumup" element={<AdminAktionHandyshopSumup />} />
            <Route path="qa" element={<QaDashboard />} />
            <Route path="dhl" element={<DhlAdminSettings />} />
            <Route path="dpd" element={<DpdAdminSettings />} />
            <Route path="licenses" element={<Navigate to="/admin/unified-licenses" replace />} />
            <Route path="vat-rates" element={<VatRatesManagement />} />
            {/* Unified Auth & License System */}
            <Route path="users" element={<UsersManagement />} />
            <Route path="users/:id" element={<AdminUserDetail />} />
            <Route path="unified-licenses" element={<UnifiedLicensesManagement />} />
            <Route path="support" element={<SupportTicketsManagement />} />
            <Route path="app-notifications" element={<AppNotificationsManagement />} />
            <Route path="newsletter" element={<NewsletterManagement />} />
            <Route path="devices" element={<DevicesManagement />} />
            <Route path="translate-pricing" element={<AdminTranslatePricing />} />
            <Route path="pricing" element={<PricingManagement />} />
            <Route path="e-numbers" element={<ENumberManagement />} />
            <Route path="email-accounts" element={<EmailAccountsManagement />} />
            <Route path="mailbox" element={<AdminMailbox />} />
            <Route path="campaigns" element={<AdminCampaigns />} />
            <Route path="marketing/social-media" element={<SocialMediaManagement />} />
            <Route path="seo-analytics" element={<SeoAnalytics />} />
            <Route path="ratgeber-redaktion" element={<RatgeberRedaktion />} />
            <Route path="super-admin-users" element={<SuperAdminUsers />} />
            <Route path="profile" element={<AdminProfile />} />
            <Route path="sync-debug" element={<SyncDebug />} />
            <Route path="vorhaben" element={<VorhabenBoard />} />
          </Route>

          {/* Regular App Routes (Protected) */}
          <Route path="/" element={<AuthGuard><FeatureGateProvider><Layout /></FeatureGateProvider></AuthGuard>}>
            <Route index element={<Dashboard />} />

            {/* Profile */}
            <Route path="profile" element={<ProfilePage />} />

            {/* Articles */}
            <Route path="articles" element={<ArticlesListNew />} />
            <Route path="articles/:id" element={<ArticleDetail />} />
            <Route path="articles/hazmat" element={<HazmatOverview />} />
            <Route path="articles/clearance" element={<ClearanceCandidates />} />
            <Route path="articles/lmiv-review" element={<LmivReviewList />} />
            <Route
              path="articles/fotoauftraege"
              element={
                <PermissionGate module="articles" operation="edit">
                  <Fotoauftraege />
                </PermissionGate>
              }
            />

            {/* Listings */}
            <Route path="listings" element={<ListingsOverview />} />

            {/* WooCommerce Integration */}
            <Route path="integrations/woocommerce" element={<WooCommerceDashboard />} />
            <Route path="integrations/woocommerce/connections" element={<WooCommerceConnections />} />
            <Route path="integrations/woocommerce/products" element={<WooCommerceProducts />} />
            <Route path="integrations/woocommerce/orders" element={<WooCommerceOrders />} />
            <Route path="integrations/woocommerce/bom" element={<WooCommerceBom />} />

            {/* Shopware 6 Integration */}
            <Route path="integrations/shopware" element={<ShopwareDashboard />} />
            <Route path="integrations/shopware/connections" element={<ShopwareConnections />} />
            <Route path="integrations/shopware/products" element={<ShopwareProducts />} />
            <Route path="integrations/shopware/orders" element={<ShopwareOrders />} />
            <Route path="integrations/shopware/bom" element={<ShopwareBom />} />

            {/* Shopify Integration */}
            <Route path="integrations/shopify" element={<ShopifyIntegration />} />

            {/* Magento 2 Integration */}
            <Route path="integrations/magento" element={<MagentoDashboard />} />
            <Route path="integrations/magento/connections" element={<MagentoConnections />} />
            <Route path="integrations/magento/products" element={<MagentoProducts />} />
            <Route path="integrations/magento/orders" element={<MagentoOrders />} />

            {/* Odoo Integration */}
            <Route path="integrations/odoo" element={<OdooDashboard />} />
            <Route path="integrations/odoo/connections" element={<OdooConnections />} />
            <Route path="integrations/odoo/mappings" element={<OdooMappings />} />
            <Route path="integrations/odoo/errors" element={<OdooErrors />} />
            <Route path="integrations/odoo/log" element={<OdooSyncLog />} />
            <Route path="integrations/odoo/migration" element={<OdooMigration />} />

            {/* JTL-Fulfillment-Network — der OAuth-Callback leitet hierher zurueck */}
            <Route path="integrations/ffn" element={<FfnIntegration />} />

            {/* Etsy Integration */}
            <Route path="integrations/etsy" element={<EtsyDashboard />} />
            <Route path="integrations/etsy/connections" element={<EtsyConnections />} />
            <Route path="integrations/etsy/listings" element={<EtsyListings />} />
            <Route path="integrations/etsy/listings/new" element={<EtsyListingDetail />} />
            <Route path="integrations/etsy/listings/:listingId" element={<EtsyListingDetail />} />
            <Route path="integrations/etsy/orders" element={<EtsyOrders />} />
            <Route path="integrations/etsy/settings" element={<EtsySettings />} />

            {/* Shopee Integration */}
            <Route path="integrations/shopee" element={<ShopeeDashboard />} />
            <Route path="integrations/shopee/connections" element={<ShopeeConnections />} />
            <Route path="integrations/shopee/products" element={<ShopeeProducts />} />
            <Route path="integrations/shopee/orders" element={<ShopeeOrders />} />
            <Route path="integrations/shopee/settings" element={<ShopeeSettings />} />

            {/* Whatnot Integration (Beta, V-1727) */}
            <Route path="integrations/whatnot" element={<WhatnotOverview />} />

            {/* Google Local Inventory (BISLocal) */}
            <Route path="integrations/google-local" element={<GoogleLocalDashboard />} />
            <Route path="integrations/google-local/settings" element={<GoogleLocalSettings />} />
            <Route path="integrations/google-local/articles" element={<GoogleLocalArticles />} />

            {/* Orders */}
            <Route path="orders" element={<OrdersList />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="postfach" element={<Mailbox />} />
            <Route path="orders/import" element={<OrderImportSources />} />
            <Route path="orders/import/templates" element={<OrderImportTemplates />} />
            <Route path="orders/import/history" element={<OrderImportHistory />} />
            <Route path="orders/repairs" element={<RepairOrdersOverview />} />
            {/* Angebote — `new` läuft über dieselbe Route wie eine Kennung: EIN Route-Eintrag,
                damit die Maske nach dem ersten Speichern nicht neu eingehängt wird. Der Riegel
                sitzt im Server (`OFFERS_*`), die Route verhindert nur das versehentliche Betreten. */}
            <Route
              path="orders/offers"
              element={
                <PermissionGate module="offers">
                  <OffersList />
                </PermissionGate>
              }
            />
            <Route
              path="orders/offers/:id"
              element={
                <PermissionGate module="offers">
                  <OfferDetail />
                </PermissionGate>
              }
            />
            <Route path="orders/sync-review" element={<OrderSyncReview />} />
            {/* 🚨 Die Route traegt denselben Riegel wie der Endpunkt: Nur wer selbst abweichende
                Preise setzen darf, darf sie auch freigeben — sonst waere die Freigabe ein Umweg
                um genau das Recht, das sie absichern soll. Ein ausgeblendeter Menuepunkt allein
                ist kein Schutz; der eigentliche Riegel sitzt im Server. */}
            <Route
              path="orders/price-approvals"
              element={
                <PermissionGate module="price_override" operation="edit">
                  <PriceApprovalList />
                </PermissionGate>
              }
            />
            {/* V-2382: Offene Erstattungen — Route vor `orders/:id`, Recht wie die Auftragsliste. */}
            <Route
              path="orders/erstattungen"
              element={
                <PermissionGate module="orders" operation="view">
                  <ErstattungenOffen />
                </PermissionGate>
              }
            />
            <Route path="returns" element={<ReturnsOverview />} />
            <Route path="returns/workshop-orders" element={<WorkshopOrdersOverview />} />

            {/* Tasks */}
            <Route path="tasks" element={<TasksList />} />
            <Route path="tasks/board" element={<TasksBoard />} />
            <Route path="tasks/calendar" element={<TasksCalendar />} />
            <Route path="tasks/my" element={<MyTasks />} />

            {/* Workflows / Automation (also accessible from Settings) */}
            <Route path="workflows" element={<WorkflowsList />} />
            <Route path="workflows/:id" element={<WorkflowDetail />} />

            {/* CRM (Customers + Newsletter + System Campaigns) */}
            <Route path="crm" element={<CrmLayout />}>
              <Route path="overview" element={<CrmOverview />} />
              {/* Vertiefung der ABC-Kacheln aus der Uebersicht: Klasse mal Kundengruppe. */}
              <Route path="abc" element={<AbcDistribution />} />
              <Route path="pipeline" element={<PipelinePage />} />
              <Route path="today" element={<TodayList />} />
              {/* Call-Modus: EIN Kunde formatfuellend, aus der Tagesliste heraus. */}
              <Route path="call" element={<CallModus />} />
              <Route path="tours" element={<TourPreviewList />} />
              <Route path="territories" element={<TerritoriesPage />} />
              <Route path="duplicates" element={<DuplicatesPage />} />
              {/* V-0206: Kundenliste und Kundenakte brauchen „Kunden ansehen“ — der Riegel sitzt im
                  Server (CUSTOMERS_VIEW), die Route verhindert nur das versehentliche Betreten. */}
              <Route path="customers" element={<PermissionGate module="customers"><CustomersList /></PermissionGate>} />
              <Route path="customers/:id" element={<PermissionGate module="customers"><CustomerDetail /></PermissionGate>} />
              <Route path="shelf-feedback" element={<ShelfFeedback />} />
              <Route path="newsletter" element={<NewsletterOverview />} />
              <Route path="system-campaigns" element={<SystemCampaignsOverview />} />
            </Route>

            {/* Legacy customer/newsletter routes for backwards compatibility */}
            <Route path="customers" element={<PermissionGate module="customers"><CustomersList /></PermissionGate>} />
            <Route path="customers/:id" element={<PermissionGate module="customers"><CustomerDetail /></PermissionGate>} />

            {/* Accounting (+ Banking tab) */}
            <Route path="accounting" element={<AccountingOverview />}>
              <Route path="invoices" element={<InvoicesList />} />
              <Route path="invoices/new" element={<InvoiceForm />} />
              <Route path="invoices/collective/new" element={<CollectiveInvoiceForm />} />
              <Route path="invoices/:id" element={<InvoiceDetail />} />
              <Route path="invoices/:id/edit" element={<InvoiceForm />} />
              <Route path="payments" element={<PaymentsOverview />} />
              <Route path="incoming" element={<IncomingInvoicesList />} />
              <Route path="incoming/:id" element={<IncomingInvoiceDetail />} />
              <Route path="datev" element={<DatevExport />} />
              <Route path="datev-online" element={<DatevOnline />} />
              <Route path="lexware-office" element={<LexwareOffice />} />
              <Route path="bookkeeping" element={<Bookkeeping />} />
              <Route path="verfahrensdoku" element={<Verfahrensdokumentation />} />
              <Route path="differenzbesteuerung" element={<Differenzbesteuerung />} />
              <Route path="banking" element={<BankingOverview />} />
            </Route>

            {/* Legacy banking route */}
            <Route path="banking" element={<BankingOverview />} />

            {/* BISRepricer */}
            <Route path="repricing" element={<RepricingDashboard />} />
            <Route path="repricing/rules" element={<RepricingRules />} />
            <Route path="repricing/articles" element={<RepricingArticles />} />
            <Route path="repricing/competitors" element={<CompetitorMonitoring />} />
            <Route path="repricing/crawler" element={<PriceCrawler />} />
            <Route path="repricing/analysis" element={<MarketAnalysis />} />
            <Route path="repricing/log" element={<RepricingHistory />} />

            {/* BISCalc - Preiskalkulation & Preisschilder */}
            <Route path="biscalc" element={<BISCalcDashboard />} />
            <Route path="biscalc/rules" element={<BISCalcRules />} />
            <Route path="biscalc/articles" element={<BISCalcArticles />} />
            <Route path="biscalc/log" element={<BISCalcHistory />} />
            <Route path="biscalc/labels" element={<BISCalcLabelPrint />} />
            <Route path="biscalc/labels/designer" element={<Navigate to="/settings/design-studio?category=label&type=priceLabel" replace />} />
            <Route path="biscalc/reports" element={<BISCalcReports />} />
            <Route path="biscalc/settings" element={<BISCalcSettings />} />

            {/* BISTime - Time Tracking (embedded redirect to standalone) */}
            <Route path="time-tracking" element={<Navigate to="/bistime/dashboard" replace />} />

            {/* BISJustShip - Standalone Shipping Module */}
            <Route path="bisjustship" element={<BISJustShipOverview />} />

            {/* Legacy newsletter route */}
            <Route path="newsletter" element={<NewsletterOverview />} />

            {/* Legacy routes for backwards compatibility */}
            <Route path="invoices" element={<InvoicesList />} />
            <Route path="invoices/new" element={<InvoiceForm />} />
            <Route path="invoices/collective/new" element={<CollectiveInvoiceForm />} />
            <Route path="invoices/:id" element={<InvoiceDetail />} />
            <Route path="invoices/:id/edit" element={<InvoiceForm />} />
            <Route path="incoming-invoices" element={<IncomingInvoicesList />} />
            <Route path="incoming-invoices/:id" element={<IncomingInvoiceDetail />} />
            <Route path="payments" element={<PaymentsOverview />} />

            {/* Cartons (BISCarton) */}
            <Route path="cartons/statistics" element={<CartonStatistics />} />
            <Route path="cartons/ppwr" element={<PPWRCompliance />} />

            {/* Shipping (+ Picking + Packing tabs) */}
            <Route path="shipping" element={<ShippingLayout />}>
              <Route path="overview" element={<ShippingOverview />} />
              <Route path="dpd" element={<DpdSettings />} />
              <Route path="flash-express" element={<FlashExpressSettings />} />
              <Route path="kerry-express" element={<KerryExpressSettings />} />
              <Route path="delivery-notes" element={<DeliveryNotes />} />
              <Route path="delivery-notes/settings" element={<DeliveryNoteSettings />} />
              <Route path="delivery-notes/:id" element={<DeliveryNoteDetail />} />
              <Route path="rate-comparison" element={<RateComparison />} />
              <Route path="rules" element={<ShippingRules />} />
              <Route path="costs" element={<ShippingCostReconciliation />} />
              <Route path="picking" element={<PickingOverview />} />
              <Route path="picking/:picklistId/mde" element={<PickingMDE />} />
              <Route path="picking/:picklistId/print" element={<PicklistPrint />} />
              {/* Sammellisten stehen aufklappbar in der EINEN Kommissionier-Übersicht (V-0038) */}
              <Route path="picking/waves" element={<Navigate to="/shipping/picking" replace />} />
              <Route path="picking/waves/:id" element={<Navigate to="/shipping/picking" replace />} />
              <Route path="picking/waves/:id/mde" element={<WavePickingMDE />} />
              <Route path="picking/waves/:id/packing" element={<WavePackingStation />} />
              <Route path="picking/pack-stations" element={<PackStationDashboard />} />
              <Route path="carts" element={<CartOverview />} />
              <Route path="packing" element={<PackingStation />} />
              <Route path="packing/open-drafts" element={<OpenPackDrafts />} />
            </Route>

            {/* Legacy picking/packing routes → redirect to new shipping/* paths.
                🚨 Ziel OHNE `/app`: Der Router läuft mit basename `/app/` — `/app/shipping/...` als Ziel
                landete auf `/app/app/shipping/...` (V-0651). IDs bleiben in der Weiterleitung erhalten. */}
            <Route path="picking" element={<Navigate to="/shipping/picking" replace />} />
            <Route path="picking/:picklistId/mde" element={<LegacyWeiterleitung ziel="/shipping/picking/:picklistId/mde" />} />
            <Route path="picking/:picklistId/print" element={<LegacyWeiterleitung ziel="/shipping/picking/:picklistId/print" />} />
            <Route path="picking/waves" element={<Navigate to="/shipping/picking" replace />} />
            <Route path="picking/waves/:id" element={<Navigate to="/shipping/picking" replace />} />
            <Route path="picking/waves/:id/mde" element={<LegacyWeiterleitung ziel="/shipping/picking/waves/:id/mde" />} />
            <Route path="picking/waves/:id/packing" element={<LegacyWeiterleitung ziel="/shipping/picking/waves/:id/packing" />} />
            <Route path="picking/pack-stations" element={<Navigate to="/shipping/picking/pack-stations" replace />} />
            <Route path="packing" element={<Navigate to="/shipping/packing" replace />} />

            {/* Purchasing */}
            <Route path="purchasing" element={<PurchasingDashboard />} />
            <Route path="purchasing/shopping-lists" element={<ProcurementNeeds />} />
            <Route path="purchasing/receipt-costs" element={<ReceiptCosts />} />
            <Route path="purchasing/order-suggestions" element={<OrderSuggestionsOverview />} />
            <Route path="purchasing/supplier-prices" element={<SupplierPricesManagement />} />
            <Route path="purchasing/analytics" element={<PurchasingAnalytics />} />
            <Route path="purchasing/abc-analysis" element={<AbcAnalysis />} />
            <Route path="purchasing/suppliers" element={<SupplierManagement />} />
            <Route path="purchasing/orders" element={<PurchaseOrderManagement />} />
            <Route path="purchasing/quick-price-entry" element={<QuickPriceEntry />} />

            {/* Inventory (+ Replenishment + Dropshipping tabs) */}
            <Route path="inventory" element={<InventoryLayout />}>
              <Route path="overview" element={<InventoryOverview />} />
              <Route path="locations" element={<StorageLocations />} />
              <Route path="locations/wizard" element={<StorageLocationWizard />} />
              <Route path="locations/labels" element={<StorageLocationLabelPrint />} />
              <Route path="locations/labels/designer" element={<Navigate to="/settings/design-studio?category=label&type=storageLabel" replace />} />
              <Route path="receiving" element={<GoodsReceiptOverview />} />
              <Route path="receiving/pc" element={<GoodsReceiptPC />} />
              <Route path="receiving/mde" element={<GoodsReceiptMDE />} />
              {/* Einen bestehenden Wareneingang oeffnen: offen = fortsetzen, abgeschlossen = ansehen.
                  Die Uebersicht verwies seit jeher hierher, die Route fehlte — der Knopf lief ins Leere. */}
              <Route path="receiving/:receiptId" element={<GoodsReceiptPC />} />
              <Route path="adjustment" element={<StockAdjustment />} />
              <Route path="adjustment/mde" element={<StockAdjustmentMDE />} />
              <Route path="batches" element={<BatchesOverview />} />
              <Route path="stocktake" element={<StocktakeOverview />} />
              <Route path="stocktake/new" element={<StocktakeForm />} />
              {/* Bearbeiten nutzt dasselbe Formular — die ID schaltet es in den Edit-Modus. */}
              <Route path="stocktake/:stocktakeId/edit" element={<StocktakeForm />} />
              <Route path="stocktake/:stocktakeId/mde" element={<StocktakeMDE />} />
              <Route path="stocktake/:stocktakeId/print" element={<StocktakePrint />} />
              <Route path="stocktake/:stocktakeId/review" element={<StocktakeReview />} />
              <Route path="cycle-counts" element={<CycleCountSchedule />} />
              <Route path="snapshot" element={<InventorySnapshot />} />
              <Route path="replenishment" element={<ReplenishmentOverview />} />
              <Route path="replenishment/locations" element={<LocationsManagement />} />
              <Route path="replenishment/locations/:id" element={<LocationsManagement />} />
              <Route path="replenishment/locations/:locationId/stock-levels" element={<StockLevelsEditor />} />
              <Route path="replenishment/suggestions" element={<ReplenishmentOverview />} />
              <Route path="replenishment/pick-lists" element={<ReplenishmentOverview />} />
              <Route path="replenishment/pick-lists/:id" element={<ReplenishmentOverview />} />
              <Route path="replenishment/pick-lists/:id/picking" element={<ReplenishmentMDE />} />
              <Route path="replenishment/pick-lists/:id/receiving" element={<ReceivingMDE />} />
              <Route path="dropshipping" element={<DropshipDashboard />} />
              <Route path="dropshipping/principals" element={<DropshipPrincipals />} />
              <Route path="dropshipping/principals/:id" element={<DropshipPrincipalDetail />} />
              <Route path="dropshipping/orders" element={<DropshipOrders />} />
              <Route path="dropshipping/orders/import" element={<DropshipImport />} />
              <Route path="dropshipping/mappings" element={<DropshipMappings />} />
              <Route path="dropshipping/billing" element={<DropshipBilling />} />
            </Route>

            {/* Legacy replenishment routes */}
            <Route path="replenishment" element={<ReplenishmentOverview />} />
            <Route path="replenishment/locations" element={<LocationsManagement />} />
            <Route path="replenishment/locations/:id" element={<LocationsManagement />} />
            <Route path="replenishment/locations/:locationId/stock-levels" element={<StockLevelsEditor />} />
            <Route path="replenishment/pick-lists/:id/picking" element={<ReplenishmentMDE />} />
            <Route path="replenishment/pick-lists/:id/receiving" element={<ReceivingMDE />} />

            {/* Legacy dropshipping routes */}
            <Route path="dropshipping" element={<DropshipDashboard />} />
            <Route path="dropshipping/principals" element={<DropshipPrincipals />} />
            <Route path="dropshipping/principals/:id" element={<DropshipPrincipalDetail />} />
            <Route path="dropshipping/orders" element={<DropshipOrders />} />
            <Route path="dropshipping/orders/import" element={<DropshipImport />} />
            <Route path="dropshipping/mappings" element={<DropshipMappings />} />
            <Route path="dropshipping/billing" element={<DropshipBilling />} />

            {/* Reports — Detail-Reports sind Teil von BIS Insights (15 €/Monat);
                der index (Dashboard-Quickview) sowie reine Navigations-/Listen-Seiten
                (TaxReports, SupplierReports) bleiben absichtlich frei. */}
            {/* V-1223: Berichte brauchen `reports.view` — wie der Seitenleisten-Eintrag; ohne das Recht antwortet der Server 403. */}
            {/* Steuer-Seiten (taxes, pos-taxes) genügen auch mit `accounting.view` allein — dort prüft jeder Reiter sein eigenes Recht. */}
            <Route path="reports" element={<PermissionGate module="reports" fallback={<PermissionGate module="accounting"><ReportsLayout /></PermissionGate>}><ReportsLayout /></PermissionGate>}>
              <Route element={<PermissionGate module="reports"><Outlet /></PermissionGate>}>
              <Route index element={<ReportsDashboard />} />
              <Route path="sales" element={<FeatureGate feature="bisstatistics"><SalesReports /></FeatureGate>} />
              <Route path="customers" element={<FeatureGate feature="bisstatistics"><CustomerReports /></FeatureGate>} />
              <Route path="articles" element={<FeatureGate feature="bisstatistics"><ArticleReports /></FeatureGate>} />
              <Route path="inventory" element={<FeatureGate feature="bisstatistics"><InventoryReports /></FeatureGate>} />
              <Route path="valuation" element={<FeatureGate feature="bisstatistics"><InventoryValuation /></FeatureGate>} />
              <Route path="fulfillment" element={<FeatureGate feature="bisstatistics"><FulfillmentReports /></FeatureGate>} />
              <Route path="marketplaces" element={<FeatureGate feature="bisstatistics"><MarketplaceReports /></FeatureGate>} />
              </Route>
              <Route path="taxes" element={<TaxReports />} />
              <Route element={<PermissionGate module="reports"><Outlet /></PermissionGate>}>
              <Route path="fees" element={<FeatureGate feature="bisstatistics"><FeesReport /></FeatureGate>} />
              <Route path="suppliers" element={<SupplierReports />} />
              <Route path="performance" element={<FeatureGate feature="bisstatistics"><PerformanceDashboard /></FeatureGate>} />
              <Route path="performance/employee/:id" element={<FeatureGate feature="bisstatistics"><EmployeeDetail /></FeatureGate>} />
              <Route path="performance/details" element={<FeatureGate feature="bisstatistics"><PerformanceReports /></FeatureGate>} />
              <Route path="performance/settings" element={<FeatureGate feature="bisstatistics"><PerformanceSettings /></FeatureGate>} />
              <Route path="performance/vertrieb" element={<FeatureGate feature="bisstatistics"><VertriebsScorecard /></FeatureGate>} />
              </Route>
              <Route path="financial" element={<FeatureGate feature="bisstatistics"><FinancialReports /></FeatureGate>} />
              <Route path="pos-taxes" element={<FeatureGate feature="bisstatistics"><PosReports /></FeatureGate>} />
            </Route>

            {/* Taxes / OSS */}
            <Route path="taxes" element={<TaxOverview />} />
            <Route path="taxes/classes" element={<TaxClassesManagement />} />
            {/* ZM gehört zu BIS Insights — dieselbe Feature-Prüfung (`bisremote_accounting`) wie
                UStVA/DATEV/EÜR serverseitig im DatevController (Ben-Vorgabe 17.09.2026). */}
            <Route path="taxes/zm" element={<FeatureGate feature="bisremote_accounting"><ZMReports /></FeatureGate>} />
            <Route path="taxes/warehouse-vat" element={<WarehouseVatCompliance />} />
            <Route path="taxes/proof-of-arrival" element={<ProofOfArrival />} />

            {/* Settings - all settings are now in one page */}
            {/* POS Integrations — redirects to Settings > Schnittstellen */}
            <Route path="pos-loyalty" element={<PermissionGate module="loyalty"><PosLoyalty /></PermissionGate>} />
            <Route path="pos-api-integrations" element={<Navigate to="/settings?category=integrations&section=api-webhooks" replace />} />
            <Route path="pos-jtl-integration" element={<Navigate to="/settings?category=integrations&section=jtl-wawi" replace />} />
            <Route path="pos-devices" element={<Navigate to="/settings?category=integrations&section=pos-sync" replace />} />
            <Route path="channel-sync-log" element={<Navigate to="/settings?category=integrations&section=channel-sync-log" replace />} />

            <Route path="settings" element={<SettingsOverview />} />
            {/* BIS Archiv (revisionssicheres DMS) — sichtbar/erreichbar nur mit aktiver Lizenz */}
            <Route path="bis-archiv" element={<FeatureGate feature="dms_revisionssicher"><BisArchiv /></FeatureGate>} />
            {/* Anbindungen — Status aller Schnittstellen (Basis-Funktion, kein Gate) */}
            <Route path="integration-status" element={<IntegrationStatusPage />} />
            <Route path="settings/manufacturers" element={<ManufacturerSettings />} />
            <Route path="settings/countries" element={<CountrySettings />} />
            <Route path="settings/ean-pool" element={<EanPoolSettings />} />
            <Route path="settings/ean-lists" element={<EanListSettings />} />
            <Route path="settings/gs1-prefixes" element={<Gs1PrefixSettings />} />
            <Route path="settings/gs1-digital-link" element={<Gs1DigitalLink />} />
            <Route path="settings/ai-article" element={<AiArticleSettings />} />
            <Route path="settings/design-studio" element={<InvoiceDesigner />} />
            <Route path="settings/design-studio/:id" element={<InvoiceDesigner />} />
            <Route path="settings/invoice-designer" element={<Navigate to="/settings/design-studio" replace />} />
            <Route path="settings/invoice-designer/:id" element={<Navigate to="/settings/design-studio" replace />} />
            <Route path="settings/document-templates" element={<Navigate to="/settings/design-studio" replace />} />
            <Route path="settings/document-templates/:id" element={<Navigate to="/settings/design-studio" replace />} />
            <Route path="settings/label-designer" element={<Navigate to="/settings/design-studio?category=label&type=priceLabel" replace />} />
            <Route path="settings/receipt-templates" element={<PosReceiptTemplateEditor />} />
            <Route path="settings/tenant-files" element={<TenantFiles />} />
            <Route path="settings/jtl-migration" element={<JtlMigrationWizard />} />
            <Route path="settings/datenuebernahme" element={<DatenuebernahmeWizard />} />
            <Route path="settings/stripe" element={<StripeIntegration />} />
            <Route path="settings/widerruf-bridge" element={<WiderrufBridgeSettings />} />
            <Route path="stripe/subscriptions" element={<StripeSubscriptions />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
    </ChunkErrorBoundary>
  );
}

export default App;
